<template>
  <app-layout>
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Edit Artikel Blog
      </h2>
    </template>

    <div class="py-12">
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
          <div class="p-6">
            <form @submit.prevent="submit">
              <div class="mb-6">
                <jet-label for="title" value="Judul" />
                <jet-input id="title" type="text" class="mt-1 block w-full" v-model="form.title" autofocus />
                <jet-input-error :message="form.errors.title" class="mt-2" />
              </div>

              <div class="mb-6">
                <jet-label for="excerpt" value="Ringkasan" />
                <textarea id="excerpt" class="mt-1 block w-full border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm" v-model="form.excerpt" rows="3"></textarea>
                <p class="mt-1 text-sm text-gray-500">Ringkasan singkat dari artikel blog (opsional)</p>
                <jet-input-error :message="form.errors.excerpt" class="mt-2" />
              </div>

              <div class="mb-6">
                <jet-label for="content" value="Konten" />
                <QuillEditor
                  v-model:content="form.content"
                  contentType="html"
                  theme="snow"
                  toolbar="full"
                  class="mt-1 bg-white"
                  style="height: 300px"
                />
                <jet-input-error :message="form.errors.content" class="mt-2" />
              </div>

              <div class="mb-6">
                <jet-label for="heading_image" value="Gambar Header" />
                <input type="file" id="heading_image" @change="onFileChange" class="mt-1 block w-full" />
                <p class="mt-1 text-sm text-gray-500">Ukuran yang direkomendasikan: 1200 x 630 piksel</p>
                <jet-input-error :message="form.errors.heading_image" class="mt-2" />
                <div v-if="imagePreview || post.heading_image_url" class="mt-2">
                  <p class="text-sm text-gray-500 mb-1">Pratinjau Gambar:</p>
                  <img :src="imagePreview || post.heading_image_url" class="max-w-xs rounded-md shadow-sm" />
                </div>
              </div>
              
              <div class="mb-6">
                <jet-label for="attachments" value="Lampiran" />
                <input 
                  type="file" 
                  id="attachments" 
                  @change="onAttachmentChange" 
                  class="mt-1 block w-full" 
                  multiple
                />
                <p class="mt-1 text-sm text-gray-500">Ukuran maksimal file: 10MB. Anda dapat memilih beberapa file.</p>
                <jet-input-error :message="form.errors['attachments.0']" class="mt-2" />
                
                <!-- Existing Attachments -->
                <div v-if="attachments && attachments.length > 0" class="mt-4 mb-4">
                  <p class="text-sm text-gray-700 font-medium mb-2">Lampiran yang Ada:</p>
                  <ul class="space-y-2">
                    <li v-for="attachment in attachments" :key="attachment.id" class="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div class="flex items-center">
                        <span class="mr-2 text-blue-500">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        </span>
                        <div>
                          <a :href="attachment.file_url" target="_blank" class="text-sm font-medium text-blue-600 hover:text-blue-800 truncate">
                            {{ attachment.original_filename }}
                          </a>
                          <p class="text-xs text-gray-500">{{ formatFileSize(attachment.file_size) }}</p>
                        </div>
                      </div>
                      <div class="flex items-center">
                        <a :href="attachment.file_url" target="_blank" class="text-blue-500 hover:text-blue-700 mr-3">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                        <button 
                          type="button" 
                          @click="removeExistingAttachment(attachment.id)" 
                          class="text-red-500 hover:text-red-700"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  </ul>
                </div>
                
                <!-- New Attachments -->
                <div v-if="attachmentPreviews.length > 0" class="mt-4">
                  <p class="text-sm text-gray-700 font-medium mb-2">File yang akan diunggah:</p>
                  <ul class="space-y-2">
                    <li v-for="(file, index) in attachmentPreviews" :key="index" class="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div class="flex items-center">
                        <span class="mr-2 text-blue-500">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        </span>
                        <div>
                          <p class="text-sm font-medium text-gray-700 truncate" :title="file.name">{{ file.name }}</p>
                          <p class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        @click="removeAttachment(index)" 
                        class="text-red-500 hover:text-red-700"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="flex flex-wrap mb-6 -mx-3">
                <div class="w-full md:w-1/3 px-3 mb-4 md:mb-0">
                  <div class="flex items-center">
                    <jet-checkbox id="is_in_carousel" v-model:checked="form.is_in_carousel" />
                    <jet-label for="is_in_carousel" value="Tampilkan di Carousel" class="ml-2" />
                  </div>
                  <p class="text-sm text-gray-500 mt-1">Tampilkan artikel ini di carousel homepage</p>
                </div>

                <div class="w-full md:w-1/3 px-3 mb-4 md:mb-0">
                  <div class="flex items-center">
                    <jet-checkbox id="is_pinned" v-model:checked="form.is_pinned" />
                    <jet-label for="is_pinned" value="Sematkan Artikel" class="ml-2" />
                  </div>
                  <p class="text-sm text-gray-500 mt-1">Sematkan artikel ini di bagian atas daftar</p>
                </div>

                <div class="w-full md:w-1/3 px-3">
                  <div class="flex items-center">
                    <jet-checkbox id="is_published" v-model:checked="form.is_published" />
                    <jet-label for="is_published" value="Terbitkan Sekarang" class="ml-2" />
                  </div>
                  <p class="text-sm text-gray-500 mt-1">Jika tidak dicentang, artikel akan disimpan sebagai draft</p>
                </div>
              </div>

              <div class="flex items-center justify-end mt-6">
                <Link :href="route('blog-posts.index')" class="inline-flex items-center px-4 py-2 bg-gray-200 border border-transparent rounded-md font-semibold text-xs text-gray-800 uppercase tracking-widest hover:bg-gray-300 active:bg-gray-400 focus:outline-none focus:border-gray-500 focus:ring focus:ring-gray-300 disabled:opacity-25 transition">
                  Batal
                </Link>
                <jet-button class="ml-3" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                  Perbarui Artikel
                </jet-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script>
import { defineComponent } from 'vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import JetButton from '@/Jetstream/Button.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import { Link, useForm } from '@inertiajs/inertia-vue3';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default defineComponent({
  components: {
    AppLayout,
    JetButton,
    JetInput,
    JetCheckbox, 
    JetLabel,
    JetInputError,
    Link,
    QuillEditor,
  },
  props: {
    post: Object,
    attachments: Array,
  },
  data() {
    return {
      imagePreview: null,
      attachmentPreviews: [],
      removeAttachmentIds: [],
    };
  },
  setup(props) {
    const form = useForm({
      title: props.post.title,
      content: props.post.content,
      excerpt: props.post.excerpt || '',
      heading_image: null,
      attachments: [],
      remove_attachment_ids: [],
      is_in_carousel: props.post.is_in_carousel,
      is_pinned: props.post.is_pinned,
      is_published: props.post.is_published,
      _method: 'PUT',
    });

    return { form };
  },
  methods: {
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) {
        this.form.heading_image = null;
        this.imagePreview = null;
        return;
      }

      this.form.heading_image = file;
      
      // Create a preview
      const reader = new FileReader();
      reader.onload = (event) => {
        this.imagePreview = event.target.result;
      };
      reader.readAsDataURL(file);
    },
    
    onAttachmentChange(e) {
      const files = Array.from(e.target.files);
      if (!files.length) return;
      
      // Store files in the form
      this.form.attachments = files;
      
      // Create previews for the file list
      this.attachmentPreviews = files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type
      }));
    },
    
    removeAttachment(index) {
      // Create a new FileList without the removed attachment
      const newAttachments = [...this.form.attachments];
      newAttachments.splice(index, 1);
      this.form.attachments = newAttachments;
      
      // Update preview list
      this.attachmentPreviews.splice(index, 1);
    },
    
    removeExistingAttachment(attachmentId) {
      // Add to the list of attachments to remove
      this.removeAttachmentIds.push(attachmentId);
      this.form.remove_attachment_ids = this.removeAttachmentIds;
      
      // Update the UI to hide the removed attachment
      const index = this.attachments.findIndex(a => a.id === attachmentId);
      if (index !== -1) {
        this.attachments.splice(index, 1);
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    submit() {
      if (this.form.heading_image === null && this.imagePreview === null) {
        // If no new image is selected, don't include in the request
        this.form.delete('heading_image');
      }

      this.form.post(route('blog-posts.update', this.post.id), {
        preserveScroll: true,
        onSuccess: () => {
          this.form.reset();
          this.imagePreview = null;
        },
      });
    },
  },
});
</script>
